import {
  ExclamationCircleFilled,
  EyeOutlined,
  HistoryOutlined,
  InboxOutlined,
  ProfileOutlined,
} from '@ant-design/icons';
import { DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Empty, Modal, Popconfirm, Spin, Switch, Tooltip } from 'antd';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { type WorkflowSummary, workflowsApi } from '@/api/workflows';
import { useRoles } from '@/auth/useRoles';
import { ICON_BTN_PROPS as iconBtn } from '@/components/iconButton';
import { WorkflowCard } from '@/components/WorkflowCard';
import { handleApiError } from '@/lib/handleApiError';

/**
 * mode controls which Approved-split this list shows:
 *   - 'published' — Approved && isActive (live for advisors). Drag-to-reorder
 *     writes the canonical Order field (the order BMO ultimately sees).
 *   - 'approved'  — Approved && !isActive (approved, awaiting the Approver to
 *                   flip Active on so it goes live). Read-only order.
 *
 * Both modes render the same card shell with the same actions; only the filter,
 * the card status badge, the empty-state copy, and drag-enable differ. This is
 * the Approver mirror of the Originator's Published / Pending Publishing tab
 * split.
 *
 * Items + the Publish/Unpublish handler are owned by ApproverDashboard so the
 * two tabs share one source of truth — toggling on Published moves the card
 * to the Approved tab without needing a refresh.
 */
type Props = {
  mode: 'published' | 'approved';
  items: WorkflowSummary[] | null;
  loading: boolean;
  onTogglePublished: (id: string, isActive: boolean) => Promise<void>;
  /** Promote the staged pending-approved version to live. Fires when the
   *  Approver toggles publish on a "Pending Publishing" card whose template
   *  carries a `pendingApprovedVersionNumber`. Distinct from togglePublished
   *  so the BE can demote the prior live version atomically. */
  onPromotePending: (id: string) => Promise<void>;
  /** Reload the dashboard after a successful reorder (drops local state, refetches). */
  onReordered: () => void;
  /** Current search query — used to switch the empty-state copy between
   *  "tab genuinely empty" and "no matches for search". Empty string means
   *  the search box is empty. */
  searchQuery?: string;
  /** Callback the search-empty state calls to clear the search input. */
  onClearSearch?: () => void;
  /** Retire the workflow. Approved is one of the two archivable statuses. */
  onArchive?: (id: string) => void | Promise<void>;
};

// Stable across renders so useSensors doesn't reinit on every paint. Same 8-px
// activation distance used by the Originator's WorkflowList — keeps a click on
// an action button from triggering a drag.
const POINTER_SENSOR_OPTIONS = { activationConstraint: { distance: 8 } } as const;

/**
 * Drag-wrap for a single Published card. Identical pattern to the Originator's
 * SortablePublishedCard — pulled here verbatim since cross-file extraction would
 * tangle two surfaces that may diverge later.
 */
function SortablePublishedCard({ id, children }: { id: string; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
        zIndex: isDragging ? 2 : 'auto',
      }}
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
}

export function PublishedWorkflows({
  mode,
  items,
  loading,
  onTogglePublished,
  onPromotePending,
  onReordered,
  searchQuery = '',
  onClearSearch,
  onArchive,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { oid } = useRoles();
  const sensors = useSensors(useSensor(PointerSensor, POINTER_SENSOR_OPTIONS));
  // Optimistic local order so the drop feels instant. Reset when the parent
  // refetches (we resort `visible` below from the parent's items each render
  // when no local override is set).
  const [localOrder, setLocalOrder] = useState<string[] | null>(null);

  if (loading && items === null) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }
  // GET /workflows returns the whole template set; filter to the published baseline
  // this tab represents (currentVersionNumber > 0 means the workflow has been approved
  // at least once), then split by isActive.
  //
  // 2026-06-29 staging: a workflow can simultaneously have a live v1 (isActive=true)
  // AND a staged v2 (pendingApprovedVersionNumber>0). Such a workflow appears on
  // BOTH tabs: Published (showing v1) AND Approved Workflows (showing v2). The
  // Approver promotes v2 from the Approved tab via the toggle, which demotes v1
  // and removes both copies from their previous tabs.
  const published = (items ?? []).filter((w) => w.currentVersionNumber > 0);
  const splitForMode =
    mode === 'published'
      ? published.filter((w) => w.isActive)
      : published.filter((w) => !w.isActive || w.pendingApprovedVersionNumber);

  // Apply the optimistic local order when present (only meaningful in 'published'
  // mode). Cards added since the last drag (or removed) fall through gracefully.
  const visible =
    localOrder && mode === 'published'
      ? [
          ...localOrder
            .map((id) => splitForMode.find((w) => w.id === id))
            .filter((w): w is WorkflowSummary => Boolean(w)),
          ...splitForMode.filter((w) => !localOrder.includes(w.id)),
        ]
      : splitForMode;

  const canReorder = mode === 'published' && visible.length > 1;

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = visible.findIndex((w) => w.id === active.id);
    const newIdx = visible.findIndex((w) => w.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(visible, oldIdx, newIdx);
    setLocalOrder(next.map((w) => w.id));
    try {
      await workflowsApi.reorderPublished(next.map((w, index) => ({ id: w.id, order: index })));
      // Drop the optimistic override + tell the parent to refetch so the canonical
      // order from the BE is what we render going forward.
      setLocalOrder(null);
      onReordered();
    } catch (err) {
      // Revert optimistic order on failure.
      setLocalOrder(null);
      handleApiError(err, t, t('workflow.list.reorderFailed'));
    }
  };

  if (visible.length === 0) {
    // Two empty states (INFLOWS-107 AC03):
    //   • Search active + zero matches → "no matches for '<query>'" copy + a
    //     Clear-search button so the reviewer can bail out of a bad query in
    //     one click, mirroring the Originator dashboard's behaviour.
    //   • Search empty + tab empty → keep the original mode-specific copy
    //     that explains WHY the tab is empty for this account.
    const needle = searchQuery.trim();
    return (
      <Empty
        description={
          needle
            ? t('workflow.list.noneMatchSearch', {
                query: needle,
                defaultValue: 'No workflows match "{{query}}" on this tab.',
              })
            : mode === 'published'
              ? t('approver.published.empty')
              : t('approver.approved.empty')
        }
      >
        {needle && onClearSearch && (
          <Button onClick={onClearSearch}>
            {t('workflow.list.clearSearch', { defaultValue: 'Clear search' })}
          </Button>
        )}
      </Empty>
    );
  }

  const card = (w: WorkflowSummary) => {
    // The Approved tab represents "ready to be published next." When the
    // template carries a pendingApprovedVersionNumber the card represents
    // THAT version (v2), and the toggle promotes it (demoting v1 in the
    // process). Otherwise it represents the first-time approval awaiting
    // its initial publish, and the toggle is the regular SetActive flip.
    const isStagedPending = mode === 'approved' && Boolean(w.pendingApprovedVersionNumber);
    const cardVersionNumber = isStagedPending
      ? (w.pendingApprovedVersionNumber as number)
      : w.currentVersionNumber;
    const cardVersionLabel = isStagedPending
      ? (w.pendingApprovedVersionLabel ?? `v${w.pendingApprovedVersionNumber}`)
      : w.currentVersionLabel;
    // AC2 on "Publish an approved request": the user who created the workflow
    // cannot publish (or unpublish) their own submission. BE refuses the call
    // with 403; hiding the toggle here keeps the approver-owner from seeing
    // an affordance they can't use. A different-user approver still sees the
    // toggle normally.
    const isSelfSubmission = !!oid && oid === w.ownerOid;
    // Confirm-copy differs by direction (AC4). Compute once so the
    // ConfirmPopover copy stays close to the branch that fires it.
    const willActivate = isStagedPending || !w.isActive;
    const confirmTitle = willActivate
      ? t('approver.publish.confirmPublishTitle', { defaultValue: 'Publish this workflow?' })
      : t('approver.publish.confirmUnpublishTitle', { defaultValue: 'Unpublish this workflow?' });
    const confirmDesc = willActivate
      ? t('approver.publish.confirmPublishDesc', {
          defaultValue:
            'It will become live for advisors immediately. Recipients you have wired in will be notified.',
        })
      : t('approver.publish.confirmUnpublishDesc', {
          defaultValue: 'Advisors will lose access to this workflow immediately.',
        });
    return (
      <WorkflowCard
        key={`${w.id}-${mode}`}
        workflow={w}
        versionLabel={cardVersionLabel}
        status={w.isActive && mode === 'published' ? 'Published' : 'PendingPublishing'}
        actions={
          <>
            <Tooltip title={t('workflow.builder.preview')}>
              <Button
                {...iconBtn}
                icon={<EyeOutlined />}
                onClick={() =>
                  // Eye opens THIS card's version preview — the live v1 on
                  // the Published tab, or the staged v2 on the Approved tab.
                  navigate(`/approver/workflows/${w.id}/preview?version=${cardVersionNumber}`)
                }
              />
            </Tooltip>
            <Tooltip title={t('workflow.list.viewDetails')}>
              <Button
                {...iconBtn}
                icon={<ProfileOutlined />}
                onClick={() =>
                  navigate(`/approver/workflows/${w.id}/versions/${cardVersionNumber}`)
                }
              />
            </Tooltip>
            <Tooltip title={t('workflow.list.versions')}>
              <Button
                {...iconBtn}
                icon={<HistoryOutlined />}
                onClick={() => navigate(`/approver/workflows/${w.id}/versions`)}
              />
            </Tooltip>
            {/* Archive — hidden while the workflow is LIVE. Advisors are using
                the published version; archiving would pull it out from under
                them, so it has to be unpublished first (2026-08-04: "if the
                workflow is published and it is under published workflows tab,
                there should be no archive button at all").

                The condition mirrors the domain guard exactly rather than
                keying off `mode`. The Published tab IS `isActive &&
                currentVersionNumber > 0`, so the button vanishes there — but
                the Approved tab can also hold a live v1 carrying a staged v2
                (`pendingApprovedVersionNumber`), and a tab-based test would
                leave a button there that the server rejects. Offering an
                action that always errors is the bug being fixed; don't
                reintroduce it one tab over. */}
            {!(w.isActive && w.currentVersionNumber > 0) && (
              <Popconfirm
                title={t('workflow.archive.archiveTitle', {
                  defaultValue: 'Archive this workflow?',
                })}
                description={t('workflow.archive.archiveConfirm', {
                  defaultValue:
                    'It leaves the dashboard but is not deleted. You can restore it from the Archive page.',
                })}
                okText={t('workflow.archive.archive', { defaultValue: 'Archive' })}
                cancelText={t('common.cancel', { defaultValue: 'Cancel' })}
                onConfirm={() => onArchive?.(w.id)}
              >
                <Tooltip title={t('workflow.archive.archive', { defaultValue: 'Archive' })}>
                  <Button {...iconBtn} icon={<InboxOutlined />} />
                </Tooltip>
              </Popconfirm>
            )}
            {/* Toggle semantics differ by card type:
                  - 'published' card: deactivate the live workflow.
                  - 'approved' card on first-time pending publish: activate.
                  - 'approved' card with a staged pending: promote v2 → live.
                  On self-submissions per AC2 the switch is replaced with a
                  disabled-info marker so the approver-owner sees WHY they
                  can't act — hiding the whole toggle left them wondering. */}
            {isSelfSubmission ? (
              <Tooltip title={t('approver.publish.selfPublishBlocked')}>
                <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center' }}>
                  <Switch size="small" checked={!isStagedPending && w.isActive} disabled />
                </span>
              </Tooltip>
            ) : (
              <Tooltip
                title={willActivate ? t('workflow.list.activate') : t('workflow.list.deactivate')}
              >
                <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center' }}>
                  <Switch
                    size="small"
                    // Fully controlled — the visual state only advances after the
                    // Modal.confirm resolves + the parent's setWorkflows patch
                    // returns. Cancelling the modal leaves the switch as-is.
                    checked={!isStagedPending && w.isActive}
                    onChange={() => {
                      // AC4 on "Publish an approved request": confirmation
                      // prompt before every publish AND unpublish so a single
                      // stray click can't ship a workflow live or take one
                      // down. Distinct copy per direction — see confirmTitle
                      // / confirmDesc above. Modal.confirm blocks until user
                      // decides; onOk fires the actual mutation.
                      Modal.confirm({
                        title: confirmTitle,
                        content: confirmDesc,
                        icon: (
                          <ExclamationCircleFilled style={{ fontSize: 22, color: '#faad14' }} />
                        ),
                        okText: t('common.yes'),
                        cancelText: t('common.no'),
                        // willActivate implies isStagedPending → promote path,
                        // otherwise fall through to the plain SetActive flip.
                        // (`isStagedPending` is a subset of willActivate, so
                        // ANDing them together was a tautology.)
                        onOk: () =>
                          isStagedPending
                            ? onPromotePending(w.id)
                            : onTogglePublished(w.id, willActivate),
                      });
                    }}
                  />
                </span>
              </Tooltip>
            )}
          </>
        }
      />
    );
  };

  const grid: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 16,
  };

  if (!canReorder) {
    return <div style={grid}>{visible.map(card)}</div>;
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <SortableContext items={visible.map((w) => w.id)} strategy={rectSortingStrategy}>
        <div style={grid}>
          {visible.map((w) => (
            <SortablePublishedCard key={w.id} id={w.id}>
              {card(w)}
            </SortablePublishedCard>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
